import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { selectToasts, removeToast } from '../../features/ui/uiSlice';
import { CheckCircleIcon, AlertIcon, InfoIcon, XIcon } from '../atoms/Icon';

/**
 * Organism: ToastContainer
 * Renders a stack of toasts via Redux uiSlice
 */

const getTypeConfig = (t) => ({
  success: {
    icon: <CheckCircleIcon className="h-5 w-5" />,
    container: 'bg-white border-l-4 border-emerald-500',
    icon_bg: 'text-emerald-600',
    title: t('common.success'),
  },
  error: {
    icon: <AlertIcon className="h-5 w-5" />,
    container: 'bg-white border-l-4 border-red-500',
    icon_bg: 'text-red-600',
    title: t('common.error'),
  },
  info: {
    icon: <InfoIcon className="h-5 w-5" />,
    container: 'bg-white border-l-4 border-blue-500',
    icon_bg: 'text-blue-600',
    title: t('common.info'),
  },
  warning: {
    icon: <AlertIcon className="h-5 w-5" />,
    container: 'bg-white border-l-4 border-amber-500',
    icon_bg: 'text-amber-600',
    title: t('common.warning'),
  },
});

const Toast = ({ id, message, type = 'success', duration = 2000, onDismiss }) => {
  const { t } = useTranslation();
  const typeConfig = getTypeConfig(t);
  const cfg = typeConfig[type] || typeConfig.success;

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), duration);
    return () => clearTimeout(timer);
  }, [id, duration, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80, scale: 0.95 }}
      animate={{ opacity: 1, x: 0,  scale: 1 }}
      exit={{   opacity: 0, x: 80,  scale: 0.95 }}
      transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
      className={clsx(
        'flex items-start gap-3 w-80 rounded-xl shadow-card-lg px-4 py-3.5',
        cfg.container
      )}
    >
      <span className={clsx('mt-0.5 flex-shrink-0', cfg.icon_bg)}>{cfg.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink">{cfg.title}</p>
        <p className="text-sm text-ink-secondary mt-0.5 leading-snug">{message}</p>
      </div>
      <button
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 mt-0.5 p-0.5 rounded text-ink-tertiary hover:text-ink transition-colors"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
};

const ToastContainer = () => {
  const toasts = useSelector(selectToasts);
  const dispatch = useDispatch();
  const onDismiss = useCallback((id) => dispatch(removeToast(id)), [dispatch]);

  return createPortal(
    <div
      aria-live="polite"
      className="fixed top-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none"
    >
      <AnimatePresence mode="sync">
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <Toast {...t} onDismiss={onDismiss} />
          </div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default ToastContainer;
